import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

export interface ProtectedRouteProps {
  role: 'user' | 'doctor' | 'admin';
  children: React.ReactNode;
}

// ===================== ユーティリティ =====================
// JWT の payload を安全にデコード（失敗時は null）
const decodeJwtPayload = (token: string): null | { exp?: number; [k: string]: any } => {
  try {
    const pure = token.startsWith('Bearer ') ? token.slice(7) : token;
    const parts = pure.split('.');
    if (parts.length < 2) return null;
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
};

// JWT の有効期限チェック（30秒のスキューを許容）
const isJwtExpired = (token: string): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false; // exp が無いトークンは期限不明→ここでは未期限切れ扱い
  const nowSec = Math.floor(Date.now() / 1000);
  const skew = 30; // 30 秒の猶予
  return nowSec >= payload.exp - skew;
};

// ロールの型ガード
const isValidRole = (v: unknown): v is 'user' | 'doctor' | 'admin' =>
  v === 'user' || v === 'doctor' || v === 'admin';

// ストレージキー（typo防止）
const LS_TOKEN = 'token';
const LS_ROLE = 'role';
// =========================================================

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ role, children }) => {
  const location = useLocation();

  // storage 変更に追従（別タブのログイン/ログアウト反映）
  const [, setAuthVersion] = useState(0);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_TOKEN || e.key === LS_ROLE) {
        setAuthVersion((v) => v + 1);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // 現在の認証情報を取得
  const rawToken = (localStorage.getItem(LS_TOKEN) || '').trim();
  const token = rawToken || null;
  const storedRoleRaw = (localStorage.getItem(LS_ROLE) || '').trim();
  const storedRole = isValidRole(storedRoleRaw) ? storedRoleRaw : null;

  // ===================== デバッグ出力（機密NGのため無効化） =====================
  // console.log('🛡️ Token:', token);       // ← 本番では出力しないこと
  // console.log('🛡️ Role:', storedRole);   // ← 本番では出力しないこと
  // ===========================================================================

  // 1) 未ログイン：対象ロールに応じて専用ログインへ
  if (!token || !storedRole) {
    if (role === 'doctor') {
      return <Navigate to="/login/doctor" state={{ from: location }} replace />;
    }
    if (role === 'admin') {
      return <Navigate to="/login/admin" state={{ from: location }} replace />;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 2) トークン期限切れ：ローカルをクリアしてログインへ
  if (isJwtExpired(token)) {
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_ROLE);
    if (role === 'doctor') {
      return <Navigate to="/login/doctor" state={{ from: location }} replace />;
    }
    if (role === 'admin') {
      return <Navigate to="/login/admin" state={{ from: location }} replace />;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3) ロール不一致：自身のダッシュボードへ強制送客
  if (storedRole !== role) {
    switch (storedRole) {
      case 'doctor':
        return <Navigate to="/doctor/dashboard" replace />;
      case 'admin':
        return <Navigate to="/admin/dashboard" replace />;
      case 'user':
        return <Navigate to="/mypage" replace />;
      default:
        // 念のため：不正値はクリアしてログインへ
        localStorage.removeItem(LS_TOKEN);
        localStorage.removeItem(LS_ROLE);
        return <Navigate to="/login" replace />;
    }
  }

  // ✅ 認証OK & ロール一致：アクセス許可
  return <>{children}</>;
};

export default ProtectedRoute;
